/*
 * WP-10 step-up gate (§14.7): sensitive mutations (submit/cancel pickup,
 * preferences, passkey/session revoke, logout-all) require a fresh step-up.
 * withStepUp(fn) runs fn; on PORTAL_STEP_UP_REQUIRED it opens the step-up
 * sheet (passkey first, code fallback) and retries fn once.
 */
import { useCallback, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isPortalApiError } from '../api/portal-api';
import { otpStepUpStart, otpStepUpVerify, passkeyStepUp, toPasskeyError } from './passkey';
import { useSession } from './session-context';
import { StepUpCancelledError, StepUpContext } from './step-up-context';
import { BottomSheet } from '../components/sheet';
import { PrimaryButton, TextLink } from '../components/primitives';
import { errorText } from '../utils/error-text';

export function StepUpProvider({ children }: { children: ReactNode }) {
  const { stepUpValid, refresh } = useSession();
  const [open, setOpen] = useState(false);
  const resolver = useRef<{ ok: () => void; fail: (e: Error) => void } | null>(null);

  // `stepUpValid` is a cached client flag; the server can expire the grant
  // first (300s TTL), so a 403 STEP_UP_REQUIRED from withStepUp must force the
  // sheet even when the flag still says warm — otherwise the retry just fails.
  const requestStepUp = useCallback(
    async (force = false): Promise<void> => {
      if (stepUpValid && !force) return;
      return new Promise<void>((resolve, reject) => {
        resolver.current = {
          ok: () => resolve(),
          fail: (e) => reject(e),
        };
        setOpen(true);
      });
    },
    [stepUpValid],
  );

  const complete = useCallback(async () => {
    setOpen(false);
    await refresh().catch(() => {});
    resolver.current?.ok();
    resolver.current = null;
  }, [refresh]);

  const cancel = useCallback(() => {
    setOpen(false);
    resolver.current?.fail(new StepUpCancelledError());
    resolver.current = null;
  }, []);

  const requireStepUp = useCallback(async () => {
    await requestStepUp();
  }, [requestStepUp]);

  const withStepUp = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      try {
        return await fn();
      } catch (err) {
        if (!isPortalApiError(err, 'PORTAL_STEP_UP_REQUIRED')) throw err;
        await requestStepUp(true);
        return fn();
      }
    },
    [requestStepUp],
  );

  const value = useMemo(() => ({ withStepUp, requireStepUp }), [withStepUp, requireStepUp]);

  return (
    <StepUpContext.Provider value={value}>
      {children}
      <StepUpSheet open={open} onDone={complete} onClose={cancel} />
    </StepUpContext.Provider>
  );
}

function StepUpSheet({
  open,
  onDone,
  onClose,
}: {
  open: boolean;
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'choose' | 'otp'>('choose');
  const [otp, setOtp] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setMode('choose');
    setOtp('');
    setChallengeId(null);
    setError(null);
  };

  const doPasskey = async () => {
    setBusy(true);
    setError(null);
    try {
      await passkeyStepUp();
      reset();
      await onDone();
    } catch (err) {
      setError(errorText(toPasskeyError(err), t));
    } finally {
      setBusy(false);
    }
  };

  const startOtp = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await otpStepUpStart();
      setChallengeId(res.challengeId);
      setSentTo(res.sentToMasked);
      setMode('otp');
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e: FormEvent) => {
    e.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await otpStepUpVerify(challengeId, otp.trim());
      reset();
      await onDone();
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={t('stepup.title')}>
      {mode === 'choose' ? (
        <div className="hp-stack">
          <p className="hp-muted">{t('stepup.body')}</p>
          <PrimaryButton onClick={doPasskey} loading={busy} data-quality-id="portal.stepup.passkey">
            {t('stepup.passkey')}
          </PrimaryButton>
          <TextLink onClick={startOtp} chevron={false} data-quality-id="portal.stepup.codeLink">
            {t('stepup.codeLink')}
          </TextLink>
        </div>
      ) : (
        <form onSubmit={verifyOtp} className="hp-stack">
          <p className="hp-muted">
            {t('auth.login.codeSent')} {sentTo}
          </p>
          <input
            className="hp-input"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            required
            aria-label={t('auth.login.codeLabel')}
            data-quality-id="portal.stepup.otp"
          />
          <PrimaryButton type="submit" loading={busy} data-quality-id="portal.stepup.verify">
            {t('common.confirm')}
          </PrimaryButton>
        </form>
      )}
      {error ? (
        <p className="hp-danger-text" role="alert">
          {error}
        </p>
      ) : null}
    </BottomSheet>
  );
}
